import type { ReactNode } from "react";
import { useParams } from "react-router-dom";
import { useLocation } from "@/hooks/use-api";
import { useDetailPageNavContext } from "@/hooks/use-detail-page-nav-context";
import { DetailPage } from "@/components/v2/detail-page";
import { DetailPageNav } from "@/components/v2/detail-page-nav";
import { DetailPageActions } from "@/components/v2/detail-page-actions";
import { KPIRow } from "@/components/v2/kpi-row";
import { PropertyHero } from "@/components/v2/property-hero";
import { formatDate } from "@/lib/utils";

type Row = Record<string, unknown>;

function InfoRow({ label, value }: { label: string; value?: string | null }) {
  if (!value) return null;
  return (
    <div className="flex items-baseline justify-between gap-2 py-1">
      <span className="typo-label text-[color:var(--color-text-lo)] shrink-0">
        {label}
      </span>
      <span className="typo-body text-[color:var(--color-text-md)] text-right truncate">
        {value}
      </span>
    </div>
  );
}

function RailSection({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <div>
      <div className="typo-label text-[color:var(--color-text-lo)] uppercase tracking-wide mb-2">
        {title}
      </div>
      {children}
    </div>
  );
}

function str(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

function num(value: unknown): number | null {
  return typeof value === "number" ? value : null;
}

export default function V2LocationDetailPage() {
  const { id } = useParams<{ id: string }>();
  const nav = useDetailPageNavContext();

  const { data, isLoading, isError } = useLocation(id ?? "");
  const location = (data as { location?: Row } | undefined)?.location;

  const address =
    str(location?.formatted_address) ??
    str(location?.street_line_1) ??
    "Location";
  const city = str(location?.city);
  const state = str(location?.state);
  const zip = str(location?.postal_code);
  const lat = num(location?.latitude);
  const lon = num(location?.longitude);
  const geocoded = lat != null && lon != null;
  const locationType = str(location?.location_type);

  const cityStateZip = [city, [state, zip].filter(Boolean).join(" ")]
    .filter(Boolean)
    .join(", ");

  return (
    <DetailPage
      isLoading={isLoading}
      isError={isError}
      isNotFound={!isLoading && !isError && !location}
      title={address}
      subtitle={cityStateZip || undefined}
      breadcrumbs={[
        { label: "Locations", href: "/locations" },
        { label: address },
      ]}
      actions={
        <>
          <DetailPageActions entityLabel="Location" />
          <DetailPageNav ids={nav.ids} currentId={nav.currentId} />
        </>
      }
      notFoundMessage={{
        title: "Location not found",
        description: "This location may have been deleted.",
      }}
    >
      <div className="flex gap-6">
        <div className="flex-1 min-w-0 space-y-5">
          {/* KPI row */}
          <KPIRow
            cards={[
              { label: "City", value: city ?? "—" },
              { label: "State", value: state ?? "—" },
              { label: "Type", value: locationType ?? "—" },
              {
                label: "Geocoded",
                value: geocoded ? "Yes" : "No",
              },
            ]}
          />

          {/* Property hero (when geocoded) */}
          {geocoded && (
            <PropertyHero
              address={address}
              lat={lat ?? undefined}
              lon={lon ?? undefined}
            />
          )}

          {/* Notes */}
          {str(location?.notes) && (
            <RailSection title="Notes">
              <p className="typo-body text-[color:var(--color-text-md)] whitespace-pre-wrap">
                {location?.notes as string}
              </p>
            </RailSection>
          )}
        </div>

        {/* Right rail */}
        <div className="w-[300px] flex-none border-l border-[color:var(--color-border)] pl-6 space-y-5">
          <RailSection title="Address">
            <InfoRow label="Street" value={str(location?.street_line_1)} />
            <InfoRow label="Street 2" value={str(location?.street_line_2)} />
            <InfoRow label="City" value={city} />
            <InfoRow label="State" value={state} />
            <InfoRow label="Zip" value={zip} />
            <InfoRow label="Country" value={str(location?.country)} />
          </RailSection>

          <RailSection title="Geocoding">
            <InfoRow
              label="Latitude"
              value={lat != null ? String(lat) : null}
            />
            <InfoRow
              label="Longitude"
              value={lon != null ? String(lon) : null}
            />
            <InfoRow label="Accuracy" value={str(location?.geocode_accuracy)} />
            <InfoRow label="Source" value={str(location?.geocode_source)} />
            <InfoRow
              label="Geocoded At"
              value={
                location?.geocoded_at
                  ? formatDate(location.geocoded_at as string)
                  : null
              }
            />
          </RailSection>

          <RailSection title="Properties">
            <InfoRow label="Type" value={locationType} />
            <InfoRow
              label="Created"
              value={
                location?.created_at
                  ? formatDate(location.created_at as string)
                  : null
              }
            />
            <InfoRow
              label="Updated"
              value={
                location?.updated_at
                  ? formatDate(location.updated_at as string)
                  : null
              }
            />
          </RailSection>
        </div>
      </div>
    </DetailPage>
  );
}
